import React, { useState, Fragment } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { ShieldCheckIcon, SearchIcon, CheckIcon, XIcon } from 'lucide-react';
// Types for Permission Matrix
type PermissionType = 'view' | 'createEdit' | 'delete' | 'approval' | 'forward';
interface ModulePermission {
  id: string;
  name: string;
  permissions: Record<PermissionType, boolean>;
  subModules?: ModulePermission[];
}
export function RolePermission() {
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'assign' | 'audit'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  // Mock Data: Roles
  const roles = [{
    id: 'ROLE-001',
    name: 'HQ Admin',
    description: 'Full system access across all stations',
    usersCount: 5,
    createdDate: '2023-01-15',
    status: 'active' as const
  }, {
    id: 'ROLE-002',
    name: 'Range Officer',
    description: 'Manage operations within a specific range',
    usersCount: 12,
    createdDate: '2023-02-20',
    status: 'active' as const
  }, {
    id: 'ROLE-003',
    name: 'Station Officer',
    description: 'Manage operations at a specific station',
    usersCount: 45,
    createdDate: '2023-03-10',
    status: 'active' as const
  }, {
    id: 'ROLE-004',
    name: 'Field Officer',
    description: 'Mobile app access for field verification',
    usersCount: 120,
    createdDate: '2023-04-05',
    status: 'active' as const
  }, {
    id: 'ROLE-005',
    name: 'Approver',
    description: 'Can only approve/reject applications',
    usersCount: 8,
    createdDate: '2023-05-12',
    status: 'active' as const
  }, {
    id: 'ROLE-006',
    name: 'Data Entry Operator',
    description: 'Can only create records, no approval rights',
    usersCount: 25,
    createdDate: '2023-06-18',
    status: 'inactive' as const
  }];
  // Mock Data: Users for Assignment
  const usersForAssignment = [{
    id: 'USR-001',
    name: 'Rahim Uddin',
    station: 'Chandpai Range',
    roles: ['Range Officer', 'Approver'],
    lastUpdated: '2024-03-25'
  }, {
    id: 'USR-002',
    name: 'Karim Sheikh',
    station: 'Katka Station',
    roles: ['Station Officer'],
    lastUpdated: '2024-03-20'
  }, {
    id: 'USR-003',
    name: 'Fatema Begum',
    station: 'Dhangmari Station',
    roles: ['Field Officer'],
    lastUpdated: '2024-03-15'
  }, {
    id: 'USR-004',
    name: 'Abdul Hakim',
    station: 'Headquarters',
    roles: ['HQ Admin'],
    lastUpdated: '2024-03-10'
  }, {
    id: 'USR-005',
    name: 'Jamal Hossain',
    station: 'Harbaria Station',
    roles: ['Data Entry Operator'],
    lastUpdated: '2024-03-05'
  }];
  // Mock Data: Audit Logs
  const auditLogs = [{
    id: 'AUD-001',
    timestamp: '2024-03-30 14:30:22',
    user: 'Admin User',
    action: 'Updated Permission',
    target: 'Role: Station Officer',
    details: 'Added Delete permission for Beneficiary Registry',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-002',
    timestamp: '2024-03-29 10:15:00',
    user: 'Admin User',
    action: 'Assigned Role',
    target: 'User: Rahim Uddin',
    details: 'Assigned Approver role',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-003',
    timestamp: '2024-03-28 16:45:10',
    user: 'System',
    action: 'Created Role',
    target: 'Role: Data Entry Operator',
    details: 'New role created with limited permissions',
    ip: '127.0.0.1'
  }, {
    id: 'AUD-004',
    timestamp: '2024-03-25 09:20:05',
    user: 'Admin User',
    action: 'Removed Role',
    target: 'User: Jamal Hossain',
    details: 'Removed Field Officer role',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-005',
    timestamp: '2024-03-20 11:10:30',
    user: 'Admin User',
    action: 'Updated Permission',
    target: 'Role: Field Officer',
    details: 'Removed Create/Edit permission for Permits',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-006',
    timestamp: '2024-03-15 15:55:45',
    user: 'Admin User',
    action: 'Assigned Role',
    target: 'User: Fatema Begum',
    details: 'Assigned Field Officer role',
    ip: '192.168.1.45'
  }, {
    id: 'AUD-007',
    timestamp: '2024-03-10 13:40:12',
    user: 'System',
    action: 'Created Role',
    target: 'Role: Approver',
    details: 'New role created for approval workflows',
    ip: '127.0.0.1'
  }, {
    id: 'AUD-008',
    timestamp: '2024-03-05 08:30:00',
    user: 'Admin User',
    action: 'Updated Permission',
    target: 'Role: HQ Admin',
    details: 'Granted all permissions across all modules',
    ip: '192.168.1.45'
  }];
  // Data Table Columns
  const roleColumns = [{
    key: 'name',
    label: 'Role Name'
  }, {
    key: 'description',
    label: 'Description'
  }, {
    key: 'usersCount',
    label: 'Users Count'
  }, {
    key: 'createdDate',
    label: 'Created Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-1739" />
  }];
  const assignColumns = [{
    key: 'name',
    label: 'User Name'
  }, {
    key: 'roles',
    label: 'Assigned Roles',
    render: (roles: string[]) => <div className="flex flex-wrap gap-1" data-id="element-1740">
          {roles.map((role, idx) => <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1741">
              {role}
            </span>)}
        </div>
  }, {
    key: 'station',
    label: 'Station/Range'
  }, {
    key: 'lastUpdated',
    label: 'Last Updated'
  }];
  const auditColumns = [{
    key: 'timestamp',
    label: 'Timestamp'
  }, {
    key: 'user',
    label: 'User'
  }, {
    key: 'action',
    label: 'Action',
    render: (value: string) => {
      let color = 'bg-gray-100 text-gray-800';
      if (value.includes('Created')) color = 'bg-green-100 text-green-800';
      if (value.includes('Updated')) color = 'bg-blue-100 text-blue-800';
      if (value.includes('Assigned')) color = 'bg-purple-100 text-purple-800';
      if (value.includes('Removed')) color = 'bg-red-100 text-red-800';
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`} data-id="element-1742">
            {value}
          </span>;
    }
  }, {
    key: 'target',
    label: 'Target'
  }, {
    key: 'details',
    label: 'Details'
  }, {
    key: 'ip',
    label: 'IP Address'
  }];
  // Filters
  const roleFilters = [{
    key: 'status',
    label: 'Status',
    options: ['Active', 'Inactive']
  }];
  const auditFilters = [{
    key: 'action',
    label: 'Action Type',
    options: ['Created Role', 'Updated Permission', 'Assigned Role', 'Removed Role']
  }];
  // Wizard Steps
  const wizardSteps = [{
    title: 'Role Info',
    description: 'Basic details of the role'
  }, {
    title: 'Permission Matrix',
    description: 'Configure module and feature access'
  }, {
    title: 'Review & Save',
    description: 'Review permissions before saving'
  }];
  // Permission Matrix State
  const initialPermissions: ModulePermission[] = [{
    id: 'dashboard',
    name: 'Dashboard',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }, {
    id: 'user_mgmt',
    name: 'User Management',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'user_list',
      name: 'User List',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'role_perm',
      name: 'Role & Permission',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'beneficiary',
    name: 'Beneficiary Registry',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'ben_list',
      name: 'Beneficiary List',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'ben_add',
      name: 'Add Beneficiary',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'ben_approve',
      name: 'Approval Queue',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'permit',
    name: 'Permit Management',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'perm_apps',
      name: 'Applications',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'perm_apply',
      name: 'Apply Permit',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'perm_track',
      name: 'Trip Tracking',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'perm_hist',
      name: 'Trip History',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'vessel',
    name: 'Vessel Management',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }, {
    id: 'compliance',
    name: 'Forest Compliance',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    },
    subModules: [{
      id: 'comp_cases',
      name: 'Case Registry',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }, {
      id: 'comp_blacklist',
      name: 'Blacklist',
      permissions: {
        view: false,
        createEdit: false,
        delete: false,
        approval: false,
        forward: false
      }
    }]
  }, {
    id: 'smartid',
    name: 'Smart ID & RFID',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }, {
    id: 'reports',
    name: 'Reports & Analytics',
    permissions: {
      view: false,
      createEdit: false,
      delete: false,
      approval: false,
      forward: false
    }
  }];
  const [permissions, setPermissions] = useState<ModulePermission[]>(initialPermissions);
  const [roleInfo, setRoleInfo] = useState({
    name: '',
    description: '',
    status: 'active'
  });
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({
    user_mgmt: true,
    beneficiary: true,
    permit: true,
    compliance: true
  });
  // Toggle module expansion
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };
  // Handle individual checkbox change
  const handlePermissionChange = (moduleId: string, subModuleId: string | null, permType: PermissionType, value: boolean) => {
    setPermissions(prev => {
      const newPerms = [...prev];
      const modIndex = newPerms.findIndex(m => m.id === moduleId);
      if (modIndex === -1) return prev;
      if (subModuleId) {
        // Update sub-module
        const subIndex = newPerms[modIndex].subModules?.findIndex(s => s.id === subModuleId) ?? -1;
        if (subIndex !== -1 && newPerms[modIndex].subModules) {
          newPerms[modIndex].subModules![subIndex].permissions[permType] = value;
          // If a sub-module gets a permission, ensure the parent module has at least 'view'
          if (value && permType !== 'view') {
            newPerms[modIndex].permissions['view'] = true;
          }
        }
      } else {
        // Update main module
        newPerms[modIndex].permissions[permType] = value;
        // If parent gets a permission, apply to all sub-modules (optional behavior, implementing for ease of use)
        if (newPerms[modIndex].subModules) {
          newPerms[modIndex].subModules!.forEach(sub => {
            sub.permissions[permType] = value;
          });
        }
      }
      return newPerms;
    });
  };
  // Handle row 'Select All'
  const handleRowSelectAll = (moduleId: string, subModuleId: string | null, value: boolean) => {
    const permTypes: PermissionType[] = ['view', 'createEdit', 'delete', 'approval', 'forward'];
    permTypes.forEach(pt => handlePermissionChange(moduleId, subModuleId, pt, value));
  };
  // Handle column 'Select All'
  const handleColumnSelectAll = (permType: PermissionType, value: boolean) => {
    permissions.forEach(mod => {
      handlePermissionChange(mod.id, null, permType, value);
    });
  };
  // Assignment State
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const handleUserSelect = (userId: string) => {
    setSelectedUser(userId);
    const user = usersForAssignment.find(u => u.id === userId);
    if (user) {
      setSelectedRoles(user.roles);
    }
  };
  const toggleRoleAssignment = (roleName: string) => {
    setSelectedRoles(prev => prev.includes(roleName) ? prev.filter(r => r !== roleName) : [...prev, roleName]);
  };
  return <div className="space-y-6" data-id="element-1743">
      <div data-id="element-1744">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1745">
          Role & Permission Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-1746">
          Configure access control and assign roles to users
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1747">
        <div className="flex gap-4" data-id="element-1748">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1749">
            Role List
          </button>
          <button onClick={() => setActiveTab('create')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'create' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1750">
            Create/Edit Role
          </button>
          <button onClick={() => setActiveTab('assign')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'assign' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1751">
            Assign Roles
          </button>
          <button onClick={() => setActiveTab('audit')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'audit' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1752">
            Audit Log
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'list' && <div className="space-y-4" data-id="element-1753">
          <div className="flex justify-end" data-id="element-1754">
            <button onClick={() => setActiveTab('create')} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] transition-colors" data-id="element-1755">
              + Add New Role
            </button>
          </div>
          <DataTable columns={roleColumns} data={roles} filters={roleFilters} onEdit={row => {
        setRoleInfo({
          name: row.name,
          description: row.description,
          status: row.status
        });
        setActiveTab('create');
        setCurrentStep(0);
      }} searchPlaceholder="Search roles..." data-id="element-1756" />
        </div>}

      {activeTab === 'create' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      alert('Role saved successfully!');
      setCurrentStep(0);
      setActiveTab('list');
      setRoleInfo({
        name: '',
        description: '',
        status: 'active'
      });
      setPermissions(initialPermissions);
    }} data-id="element-1757">
          {currentStep === 0 && <div className="space-y-4 max-w-2xl" data-id="element-1758">
              <div data-id="element-1759">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1760">
                  Role Name *
                </label>
                <input type="text" value={roleInfo.name} onChange={e => setRoleInfo({
            ...roleInfo,
            name: e.target.value
          })} placeholder="e.g., Senior Approver" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1761" />
              </div>
              <div data-id="element-1762">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1763">
                  Description
                </label>
                <textarea rows={3} value={roleInfo.description} onChange={e => setRoleInfo({
            ...roleInfo,
            description: e.target.value
          })} placeholder="Describe the purpose and access level of this role..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-1764"></textarea>
              </div>
              <div data-id="element-1765">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1766">
                  Status
                </label>
                <div className="flex gap-4" data-id="element-1767">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1768">
                    <input type="radio" name="status" value="active" checked={roleInfo.status === 'active'} onChange={() => setRoleInfo({
                ...roleInfo,
                status: 'active'
              })} className="text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-1769" />
                    <span className="text-sm" data-id="element-1770">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1771">
                    <input type="radio" name="status" value="inactive" checked={roleInfo.status === 'inactive'} onChange={() => setRoleInfo({
                ...roleInfo,
                status: 'inactive'
              })} className="text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-1772" />
                    <span className="text-sm" data-id="element-1773">Inactive</span>
                  </label>
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-1774">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 mb-4" data-id="element-1775">
                Configure permissions for{' '}
                <strong data-id="element-1776">{roleInfo.name || 'New Role'}</strong>. Checking a
                parent module's permission will automatically apply it to all
                its sub-modules.
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1777">
                <div className="overflow-x-auto" data-id="element-1778">
                  <table className="w-full text-sm text-left" data-id="element-1779">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-700" data-id="element-1780">
                      <tr data-id="element-1781">
                        <th className="px-4 py-3 font-semibold w-1/3" data-id="element-1782">
                          Module / Feature
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-1783">
                          <div data-id="element-1784">View</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('view', e.target.checked)} data-id="element-1785" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-1786">
                          <div data-id="element-1787">Create/Edit</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('createEdit', e.target.checked)} data-id="element-1788" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-1789">
                          <div data-id="element-1790">Delete</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('delete', e.target.checked)} data-id="element-1791" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-1792">
                          <div data-id="element-1793">Approval</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('approval', e.target.checked)} data-id="element-1794" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center" data-id="element-1795">
                          <div data-id="element-1796">Forward</div>
                          <input type="checkbox" className="mt-1 rounded text-[#1B5E20]" onChange={e => handleColumnSelectAll('forward', e.target.checked)} data-id="element-1797" />
                        </th>
                        <th className="px-4 py-3 font-semibold text-center border-l border-gray-200" data-id="element-1798">
                          <div data-id="element-1799">All</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-1800">
                      {permissions.map(module => <Fragment key={module.id} data-id="element-1801">
                          {/* Main Module Row */}
                          <tr className="bg-white hover:bg-gray-50" data-id="element-1802">
                            <td className="px-4 py-3 font-medium flex items-center gap-2" data-id="element-1803">
                              {module.subModules && <button onClick={() => toggleModule(module.id)} className="text-gray-500 hover:text-gray-700 w-4" data-id="element-1804">
                                  {expandedModules[module.id] ? '▼' : '▶'}
                                </button>}
                              {!module.subModules && <span className="w-4" data-id="element-1805"></span>}
                              {module.name}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1806">
                              <input type="checkbox" checked={module.permissions.view} onChange={e => handlePermissionChange(module.id, null, 'view', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-1807" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1808">
                              <input type="checkbox" checked={module.permissions.createEdit} onChange={e => handlePermissionChange(module.id, null, 'createEdit', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-1809" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1810">
                              <input type="checkbox" checked={module.permissions.delete} onChange={e => handlePermissionChange(module.id, null, 'delete', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-1811" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1812">
                              <input type="checkbox" checked={module.permissions.approval} onChange={e => handlePermissionChange(module.id, null, 'approval', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-1813" />
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1814">
                              <input type="checkbox" checked={module.permissions.forward} onChange={e => handlePermissionChange(module.id, null, 'forward', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-4 w-4" data-id="element-1815" />
                            </td>
                            <td className="px-4 py-3 text-center border-l border-gray-200 bg-gray-50" data-id="element-1816">
                              <input type="checkbox" checked={Object.values(module.permissions).every(Boolean)} onChange={e => handleRowSelectAll(module.id, null, e.target.checked)} className="rounded text-gray-600 focus:ring-gray-500 h-4 w-4" data-id="element-1817" />
                            </td>
                          </tr>

                          {/* Sub Modules Rows */}
                          {module.subModules && expandedModules[module.id] && module.subModules.map(sub => <tr key={sub.id} className="bg-gray-50/50 hover:bg-gray-100/50" data-id="element-1818">
                                <td className="px-4 py-2 pl-12 text-gray-600" data-id="element-1819">
                                  ↳ {sub.name}
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-1820">
                                  <input type="checkbox" checked={sub.permissions.view} onChange={e => handlePermissionChange(module.id, sub.id, 'view', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-1821" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-1822">
                                  <input type="checkbox" checked={sub.permissions.createEdit} onChange={e => handlePermissionChange(module.id, sub.id, 'createEdit', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-1823" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-1824">
                                  <input type="checkbox" checked={sub.permissions.delete} onChange={e => handlePermissionChange(module.id, sub.id, 'delete', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-1825" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-1826">
                                  <input type="checkbox" checked={sub.permissions.approval} onChange={e => handlePermissionChange(module.id, sub.id, 'approval', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-1827" />
                                </td>
                                <td className="px-4 py-2 text-center" data-id="element-1828">
                                  <input type="checkbox" checked={sub.permissions.forward} onChange={e => handlePermissionChange(module.id, sub.id, 'forward', e.target.checked)} className="rounded text-[#1B5E20] focus:ring-[#1B5E20] h-3.5 w-3.5" data-id="element-1829" />
                                </td>
                                <td className="px-4 py-2 text-center border-l border-gray-200" data-id="element-1830">
                                  <input type="checkbox" checked={Object.values(sub.permissions).every(Boolean)} onChange={e => handleRowSelectAll(module.id, sub.id, e.target.checked)} className="rounded text-gray-500 focus:ring-gray-500 h-3.5 w-3.5" data-id="element-1831" />
                                </td>
                              </tr>)}
                        </Fragment>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-1832">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-1833">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1834">
                  Role Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1835">
                  <div data-id="element-1836">
                    <div className="text-sm text-gray-500" data-id="element-1837">Role Name</div>
                    <div className="font-medium text-gray-900" data-id="element-1838">
                      {roleInfo.name || 'Not specified'}
                    </div>
                  </div>
                  <div data-id="element-1839">
                    <div className="text-sm text-gray-500" data-id="element-1840">Status</div>
                    <div data-id="element-1841">
                      <StatusBadge status={roleInfo.status as any} data-id="element-1842" />
                    </div>
                  </div>
                  <div className="col-span-2" data-id="element-1843">
                    <div className="text-sm text-gray-500" data-id="element-1844">Description</div>
                    <div className="text-gray-900" data-id="element-1845">
                      {roleInfo.description || 'No description provided.'}
                    </div>
                  </div>
                </div>

                <h4 className="font-semibold text-gray-900 mb-3 border-t border-gray-200 pt-4" data-id="element-1846">
                  Granted Permissions Overview
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-id="element-1847">
                  {permissions.map(mod => {
              const activePerms = Object.entries(mod.permissions).filter(([_, val]) => val).map(([key]) => key.replace('createEdit', 'Create/Edit').replace(/^\w/, c => c.toUpperCase()));
              if (activePerms.length === 0) return null;
              return <div key={mod.id} className="flex justify-between items-start py-1 border-b border-gray-100 last:border-0" data-id="element-1848">
                        <span className="font-medium text-gray-700" data-id="element-1849">
                          {mod.name}
                        </span>
                        <span className="text-gray-500 text-right" data-id="element-1850">
                          {activePerms.join(', ')}
                        </span>
                      </div>;
            })}
                </div>
              </div>
            </div>}
        </WizardForm>}

      {activeTab === 'assign' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1851">
          {/* Left Column: User Selection */}
          <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4 flex flex-col h-[600px]" data-id="element-1852">
            <h3 className="font-semibold text-gray-900 mb-4" data-id="element-1853">1. Select User</h3>
            <div className="relative mb-4" data-id="element-1854">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-1855" />
              <input type="text" placeholder="Search users..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] text-sm" data-id="element-1856" />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-2" data-id="element-1857">
              {usersForAssignment.map(user => <button key={user.id} onClick={() => handleUserSelect(user.id)} className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedUser === user.id ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1858">
                  <div className="font-medium text-gray-900" data-id="element-1859">{user.name}</div>
                  <div className="text-xs text-gray-500 mb-2" data-id="element-1860">
                    {user.station}
                  </div>
                  <div className="flex flex-wrap gap-1" data-id="element-1861">
                    {user.roles.map((role, idx) => <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600" data-id="element-1862">
                        {role}
                      </span>)}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Right Column: Role Assignment */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6 flex flex-col h-[600px]" data-id="element-1863">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-1864">
              2. Assign Roles
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-1865">
              {selectedUser ? `Select roles for ${usersForAssignment.find(u => u.id === selectedUser)?.name}` : 'Please select a user from the list first'}
            </p>

            <div className={`flex-1 overflow-y-auto pr-2 space-y-3 ${!selectedUser ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-1866">
              {roles.filter(r => r.status === 'active').map(role => {
            const isSelected = selectedRoles.includes(role.name);
            return <label key={role.id} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'border-[#1B5E20] bg-green-50/30' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-1867">
                      <div className="mt-0.5" data-id="element-1868">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRoleAssignment(role.name)} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-1869" />
                      </div>
                      <div className="flex-1" data-id="element-1870">
                        <div className="font-medium text-gray-900" data-id="element-1871">
                          {role.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1" data-id="element-1872">
                          {role.description}
                        </div>
                      </div>
                    </label>;
          })}
            </div>

            <div className="pt-4 border-t border-gray-200 mt-4 flex justify-end" data-id="element-1873">
              <button disabled={!selectedUser} onClick={() => {
            alert('Roles assigned successfully!');
            setSelectedUser(null);
            setSelectedRoles([]);
          }} className="px-6 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1874">
                Save Assignments
              </button>
            </div>
          </div>

          {/* Bottom: Current Assignments Table */}
          <div className="lg:col-span-3 mt-6" data-id="element-1875">
            <h3 className="font-semibold text-gray-900 mb-4" data-id="element-1876">
              Current Assignments Overview
            </h3>
            <DataTable columns={assignColumns} data={usersForAssignment} onEdit={row => handleUserSelect(row.id)} searchPlaceholder="Search users..." data-id="element-1877" />
          </div>
        </div>}

      {activeTab === 'audit' && <div className="space-y-4" data-id="element-1878">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900" data-id="element-1879">
            Audit logs track all changes made to roles, permissions, and user
            assignments for security and compliance purposes.
          </div>
          <DataTable columns={auditColumns} data={auditLogs} filters={auditFilters} searchPlaceholder="Search logs..." data-id="element-1880" />
        </div>}
    </div>;
}